import { waitForGrowthbookInit } from '../plugins/growthbook/growthbook';

import StartPageRouter from '../views/ContentPages/StartPageRouter';
import EmptyPage from '../views/EmptyPage';

import routes from './routes.constants';
import { boostSummaryGuard } from './boostSummaryGuard';
import { buySuperInterestGuard } from './buySuperInterestGuard';
import { isFeatureEnabled } from '../plugins/country/module';

// Lazy-loaded route components, grouped by feature via webpackChunkName so
// related views share a chunk. Heavy vendors they reach (maplibre, adyen,
// turf) are pulled into separate async vendor chunks by splitChunks (see
// vue.config.js), keeping them out of the initial bundle.

// Auth
const LoginView = () =>
  import(/* webpackChunkName: "auth" */ '../views/Login/LoginView');
const LogoutView = () =>
  import(/* webpackChunkName: "auth" */ '../views/Logout/LogoutView');

// Campaign
const ActivateCampaign = () =>
  import(
    /* webpackChunkName: "campaign" */ '../views/Campaign/ActivateCampaign'
  );

// Signup
const Wish = () =>
  import(/* webpackChunkName: "signup" */ '../views/Signup/Wish');
const WishArea = () =>
  import(/* webpackChunkName: "signup-map" */ '../views/Signup/WishArea');
const PropositionSignup = () =>
  import(/* webpackChunkName: "signup-map" */ '../views/Signup/Proposition');
const Signup = () =>
  import(/* webpackChunkName: "signup" */ '../views/Signup/Signup');
const ImageUpload = () =>
  import(/* webpackChunkName: "signup" */ '../views/Signup/ImageUpload');
const StartAnimation = () =>
  import(
    /* webpackChunkName: "onboarding" */ '../components/Abundance/StartAnimation.vue'
  );

// Swap application (SE only, see SWAP_APPLICATION feature flag)
const SwapApplication = () =>
  import(
    /* webpackChunkName: "swap-application" */ '../views/SwapApplication/SwapApplication'
  );

// Account
const PaymentHistory = () =>
  import(/* webpackChunkName: "account" */ '../views/Account/PaymentHistory');
const EditProfile = () =>
  import(/* webpackChunkName: "account" */ '@/views/Account/EditProfile');
const EditUserCredentials = () =>
  import(
    /* webpackChunkName: "account" */ '@/views/Account/EditUserCredentials'
  );

// My propositions
const MyPropositions = () =>
  import(
    /* webpackChunkName: "my-propositions" */ '../views/MyPropositions/MyPropositions2'
  );
const EditProposition = () =>
  import(
    /* webpackChunkName: "my-propositions" */ '../views/MyPropositions/EditProposition2'
  );
const EditDetailsAndDescription = () =>
  import(
    /* webpackChunkName: "my-propositions" */ '../views/MyPropositions/EditDetailsAndDescription'
  );
const EditPropositionWish = () =>
  import(
    /* webpackChunkName: "my-propositions" */ '../views/MyPropositions/EditPropositionWish2'
  );
const EditPropositionSettings = () =>
  import(
    /* webpackChunkName: "my-propositions" */ '../views/MyPropositions/EditPropositionSettings'
  );
const EditWishAreas = () =>
  import(
    /* webpackChunkName: "my-propositions" */ '../views/MyPropositions/EditWishAreas'
  );
const MyVisitors = () =>
  import(
    /* webpackChunkName: "my-propositions" */ '../views/MyPropositions/MyVisitors'
  );
const BlockedPropositions = () =>
  import(
    /* webpackChunkName: "my-propositions" */ '../views/MyPropositions/BlockedPropositions'
  );

// Proposition (public)
const Proposition = () =>
  import(
    /* webpackChunkName: "proposition" */ '../views/Proposition/Proposition'
  );
const PropositionGallery = () =>
  import(
    /* webpackChunkName: "proposition" */ '../views/Proposition/PropositionGallery'
  );
const PropositionNotFound = () =>
  import(
    /* webpackChunkName: "proposition" */ '../views/Proposition/PropositionNotFound'
  );

// Payment
const PayProposition = () =>
  import(/* webpackChunkName: "payment" */ '../views/Payments/Proposition');
const ChangePaymentMethod = () =>
  import(
    /* webpackChunkName: "payment" */ '../views/Payments/ChangePaymentMethod'
  );
const PaymentDoneProposition = () =>
  import(
    /* webpackChunkName: "payment" */ '../views/Confirmations/PaymentDoneProposition'
  );
const PaymentDoneVisibility = () =>
  import(
    /* webpackChunkName: "payment" */ '../views/Confirmations/PaymentDoneVisibility'
  );
const AppExtraVisibilityPayment = () =>
  import(
    /* webpackChunkName: "payment" */ '../views/Payments/AppExtraVisibility'
  );
const PendingPayments = () =>
  import(/* webpackChunkName: "payment" */ '../views/Payments/PendingPayments');

// Subscription
const HandleSubscription = () =>
  import(
    /* webpackChunkName: "subscription" */ '../views/Subscription/HandleSubscription'
  );
const ChangeToKlarna = () =>
  import(
    /* webpackChunkName: "subscription" */ '../views/Subscription/ChangeToKlarna'
  );
const SubscriptionCancelled = () =>
  import(
    /* webpackChunkName: "subscription" */ '../views/Confirmations/SubscriptionCancelled'
  );
const SubscriptionOfferAccepted = () =>
  import(
    /* webpackChunkName: "subscription" */ '../views/Confirmations/SubscriptionOfferAccepted'
  );
const UpdateKlarnaTokenDone = () =>
  import(
    /* webpackChunkName: "subscription" */ '../views/Confirmations/UpdateKlarnaTokenDone'
  );
const UpdateSubscriptionDone = () =>
  import(
    /* webpackChunkName: "subscription" */ '../views/Confirmations/UpdateSubscriptionDone'
  );
const AutoSubscriptionDone = () =>
  import(
    /* webpackChunkName: "subscription" */ '../views/Confirmations/AutoSubscriptionDone'
  );

// Boost
const BoostSummary = () =>
  import(/* webpackChunkName: "boost" */ '../views/Boost/BoostSummary');
const BuyBoost = () =>
  import(/* webpackChunkName: "boost" */ '../views/Boost/BuyBoost');

// Superintresse
const BuySuperInterest = () =>
  import(
    /* webpackChunkName: "super-interest" */ '../views/SuperInterest/BuySuperInterest'
  );

// Matches / swaps
const MatchesComponent = () =>
  import(/* webpackChunkName: "matches" */ '../views/Matches/MatchesRouter');
const SwapListComponent = () =>
  import(/* webpackChunkName: "swap-list" */ '../views/SwapList/SwapList2');
const SwapDetails = () =>
  import(/* webpackChunkName: "swap-details" */ '@/views/SwapDetails/Index');

// Favourites
const Favourites = () =>
  import(
    /* webpackChunkName: "favourites" */ '../views/MyFavourites/MyFavourites'
  );

// Search
const Search = () =>
  import(/* webpackChunkName: "search" */ '@/views/Search/Index');
const SearchCoverageUnsubscribe = () =>
  import(/* webpackChunkName: "search" */ '@/views/Search/UnsubscribeView');

// Blog
const BlogCollection = () =>
  import(/* webpackChunkName: "blog" */ '@/views/Blog/BlogCollection');
const BlogPost = () =>
  import(/* webpackChunkName: "blog" */ '@/views/Blog/BlogPost');

// Content / static pages
const Help = () =>
  import(/* webpackChunkName: "content" */ '../views/ContentPages/Help');
const StaticPage = () =>
  import(/* webpackChunkName: "content" */ '../views/ContentPages/StaticPage');
const RichContentPage = () =>
  import(
    /* webpackChunkName: "content" */ '../views/ContentPages/RichContentPage'
  );
const Impressum = () =>
  import(/* webpackChunkName: "content" */ '../views/ContentPages/Impressum');
const CookiePolicy = () =>
  import(
    /* webpackChunkName: "content" */ '../views/CookiePolicy/CookiePolicy'
  );
const LocalitySitemap = () =>
  import(/* webpackChunkName: "content" */ '../views/Sitemap/LocalitySitemap');
const Sitemap = () =>
  import(/* webpackChunkName: "content" */ '../views/Sitemap/Sitemap');

// Error pages
const PageNotFound = () =>
  import(/* webpackChunkName: "error" */ '../views/PageNotFound/PageNotFound');

// Navigation guard to ensure GrowthBook is initialized before signup routes
async function ensureGrowthbookReady(to, from, next) {
  await waitForGrowthbookInit();
  next();
}

const routeTable = [
  {
    path: routes.GET_STARTED,
    redirect: () => ({
      name: 'wish',
      params: { index: 1 },
      meta: { public: true, group: 'signup' }
    })
  },
  {
    path: routes.SWAP_TYPE,
    redirect: () => ({
      name: 'wish',
      params: { index: 1 },
      meta: { public: true, group: 'signup' }
    })
  },
  {
    name: 'wish',
    path: routes.WISH,
    component: Wish,
    beforeEnter: ensureGrowthbookReady,
    meta: { public: true, group: 'signup' }
  },
  {
    path: routes.WISH_AREA,
    component: WishArea,
    beforeEnter: ensureGrowthbookReady,
    meta: { public: true, group: 'signup' }
  },
  {
    path: routes.PROPOSITION_SIGNUP,
    component: PropositionSignup,
    beforeEnter: ensureGrowthbookReady,
    meta: { public: true, group: 'signup' }
  },
  {
    path: routes.SIGNUP,
    component: Signup,
    beforeEnter: ensureGrowthbookReady,
    meta: { public: true, group: 'signup' }
  },
  {
    path: routes.IMAGE_UPLOAD,
    component: ImageUpload,
    beforeEnter: ensureGrowthbookReady,
    meta: { public: true, group: 'signup' }
  },
  {
    path: routes.BLOG_REDIRECT,
    redirect: routes.NEWS
  },
  {
    path: routes.NEWS,
    component: BlogCollection,
    meta: { public: true }
  },
  {
    path: `${routes.NEWS}/:title/:id`,
    component: BlogPost,
    meta: { public: true }
  },
  // SE-only (SWAP_APPLICATION feature flag); the route paths only exist in
  // the SE/NL path files, so the routes are skipped for other countries.
  ...(isFeatureEnabled('SWAP_APPLICATION')
    ? [
        {
          path: routes.SWAP_APPLICATION,
          component: SwapApplication,
          meta: { public: true }
        }
      ]
    : []),
  {
    path: '/content/:slug',
    component: StaticPage,
    meta: { public: true }
  },
  {
    path: '/content/' + routes.GENERAL_TERMS,
    component: StaticPage,
    meta: { public: true }
  },
  {
    path: routes.GENERAL_TERMS,
    redirect: '/content/' + routes.GENERAL_TERMS,
    meta: { public: true }
  },
  {
    path: '/content/' + routes.PRIVACY_POLICY,
    component: StaticPage,
    meta: { public: true }
  },
  {
    path: routes.PRIVACY_POLICY,
    redirect: '/content/' + routes.PRIVACY_POLICY,
    meta: { public: true }
  },
  {
    path: '/c/:slug',
    component: RichContentPage,
    meta: { public: true }
  },
  {
    path: '/i/:adm1?/:adm2?/:adm3?/:pos?',
    component: LocalitySitemap,
    meta: { public: true }
  },
  {
    // Requires authentication (no `public` meta), so the router guard redirects
    // unauthenticated visitors to the login page with ?returnUrl=/campaign/activate
    // and brings them back here after they log in.
    path: routes.CAMPAIGN_ACTIVATE,
    component: ActivateCampaign
  },
  {
    path: `${routes.SWAP_LIST}/:otherUserPropositionId`,
    component: SwapListComponent
  },
  {
    path: routes.SWAP_LIST,
    component: SwapListComponent
  },
  {
    path: routes.PAYMENT_HISTORY,
    component: PaymentHistory
  },
  // `purchase`: a page where the member pays, changes how they pay or handles
  // their subscription, and the boost and Superintresse purchase pages. The
  // rating prompts keep away from them (src/utils/ratingPrompts.js).
  {
    path: routes.CHANGE_PAYMENT_METHOD,
    component: ChangePaymentMethod,
    meta: { purchase: true }
  },
  {
    path: `${routes.PAYMENT_PROPOSITION}/:propositionId`,
    component: PayProposition,
    meta: { purchase: true }
  },
  {
    path: `${routes.PAYMENT_PROPOSITION}`,
    component: PayProposition,
    meta: { purchase: true }
  },
  {
    path: routes.CHANGE_TO_KLARNA,
    exact: true,
    component: ChangeToKlarna,
    meta: { purchase: true }
  },
  {
    path: routes.PAYMENT_DONE_PROPOSITION,
    component: PaymentDoneProposition,
    meta: { purchase: true }
  },
  {
    path: routes.PAYMENT_DONE_VISIBILITY,
    component: PaymentDoneVisibility,
    meta: { purchase: true }
  },
  {
    path: routes.CHANGE_SUBSCRIPTION_DONE,
    component: UpdateSubscriptionDone,
    meta: { purchase: true }
  },
  {
    path: routes.AUTO_SUBSCRIPTION_DONE,
    component: AutoSubscriptionDone,
    meta: { purchase: true }
  },
  {
    path: routes.SUBSCRIPTION_CANCELLED,
    component: SubscriptionCancelled,
    meta: { purchase: true }
  },
  {
    path: routes.SUBSCRIPTION_OFFER_ACCEPTED,
    component: SubscriptionOfferAccepted,
    meta: { purchase: true }
  },
  {
    path: routes.UPDATE_TOKEN_DONE_KLARNA,
    component: UpdateKlarnaTokenDone,
    meta: { purchase: true }
  },
  {
    path: routes.FAVOURITES,
    component: Favourites
  },
  {
    path: routes.MY_PROPOSITIONS,
    component: MyPropositions
  },
  {
    path: `${routes.EDIT_WISH_AREAS}/:propositionId/:wishIndex`,
    component: EditWishAreas
  },
  {
    path: `${routes.EDIT_PROP_SETTINGS}/:propositionId`,
    component: EditPropositionSettings
  },
  {
    // Legacy standalone photos URL — kept alive for the mobile app's
    // "edit images" WebView, which builds this link directly. Renders the
    // unified EditProposition2 scrolled to its photos section. No redirect:
    // the app closes the screen when the WebView URL becomes /redigera/:id,
    // so the URL must stay on this path.
    path: `${routes.EDIT_PROPOSITION_PHOTOS}/:propositionId/:residenceIndex`,
    component: EditProposition
  },
  {
    path: `${routes.PROPOSITION}/:id`,
    component: Proposition,
    meta: { public: true }
  },
  {
    path: `${routes.PROPOSITION}/:id/:residenceIndex`,
    component: Proposition,
    meta: { public: true }
  },
  {
    path: `${routes.PROPOSITION_GALLERY}/:propositionId`,
    component: PropositionGallery,
    meta: { transitionName: 'slide-over' }
  },
  {
    path: `${routes.PROPOSITION_GALLERY}/:propositionId/:residenceIndex`,
    component: PropositionGallery,
    meta: { public: true, transitionName: 'slide-over' }
  },
  {
    path: `${routes.PROPOSITION_GALLERY}/:propositionId/:residenceIndex/:imageIndex`,
    component: PropositionGallery,
    meta: { public: true, transitionName: 'slide-over' }
  },
  {
    path: `${routes.HANDLE_SUBSCRIPTION}/:id`,
    component: HandleSubscription,
    meta: { purchase: true }
  },
  {
    path: `${routes.APP_EXTRA_VISIBILITY_PAYMENT}/:propositionId`,
    component: AppExtraVisibilityPayment,
    meta: { purchase: true }
  },
  {
    path: `${routes.MY_VISITORS}/:propositionId`,
    component: MyVisitors
  },
  {
    path: routes.BLOCKED_PROPOSITIONS,
    component: BlockedPropositions
  },
  {
    path: routes.EDIT_PROFILE,
    component: EditProfile
  },
  {
    path: routes.EDIT_USER_CREDENTIALS,
    component: EditUserCredentials
  },
  {
    path: routes.TRIAL_INFO,
    component: StartAnimation
  },
  {
    path: routes.FREEMIUM_INFO,
    component: StartAnimation
  },
  {
    path: `${routes.EDIT_PROPOSITION}/:propositionId`,
    component: EditProposition
  },
  {
    path: `${routes.EDIT_PROPOSITION_DETAILS_AND_DESCRIPTION}/:propositionId/:residenceIndex`,
    component: EditDetailsAndDescription
  },
  {
    path: `${routes.EDIT_PROPOSITION_WISHES}/:propositionId`,
    component: EditPropositionWish
  },
  {
    path: `${routes.EDIT_PROPOSITION_SECOND_WISHES}/:propositionId`,
    component: EditPropositionWish
  },
  {
    path: routes.OLD_SWAP_CHAIN_MANUAL,
    redirect: `${routes.SWAP_DETAILS}/:pid1/:pid2`
  },
  {
    path: routes.OLD_SWAP_CHAIN_DIRECT,
    redirect: `${routes.SWAP_DETAILS}/:pid1/:pid2`
  },
  {
    path: routes.OLD_SWAP_CHAIN_TRIANGLE,
    redirect: p => {
      const { query } = p;
      if (query.ad3) {
        return `${routes.SWAP_DETAILS}/triangle/:pid1/:pid2/${query.ad3}`;
      }
      return `${routes.SWAP_DETAILS}/triangle/:pid1/:pid2`;
    }
  },
  {
    path: `${routes.SWAP_DETAILS}/:propositionIdAPart/:propositionIdBPart`,
    component: SwapDetails
  },
  {
    path: `${routes.SWAP_DETAILS}/triangle/:propositionIdAPart/:propositionIdBPart/:propositionIdCPart?`,
    component: SwapDetails
  },
  {
    path: routes.LOGIN,
    alias: [routes.LOGIN_RESET_PASSWORD],
    component: LoginView,
    meta: { public: true }
  },
  // The automail welcome mails link to per-country "create password" paths
  // that never existed here — send them to the login route, which shows the
  // set-password form when api_reset_token is in the query. Registered as
  // literals on every domain (each mail link only occurs on its own domain).
  { path: '/skapa-losenord', redirect: routes.LOGIN },
  { path: '/maak-wachtwoord', redirect: routes.LOGIN },
  { path: '/passwort-erstellen', redirect: routes.LOGIN },
  {
    path: routes.LOGOUT,
    component: LogoutView,
    meta: { public: true }
  },
  {
    path: routes.PAYMENT_PENDING,
    component: PendingPayments,
    meta: { purchase: true }
  },
  {
    name: 'search',
    path: routes.SEARCH,
    component: Search,
    meta: { public: true }
  },
  {
    path: routes.MATCHES,
    component: MatchesComponent
  },
  {
    path: `${routes.MATCHES}/:id`,
    component: MatchesComponent
  },
  // "Hjälp och feedback": the contact pages, then the start page, a topic
  // (/hjalp/:topic) or a question (/hjalp/:topic/:question). One component,
  // so moving between them keeps the CMS content (views/ContentPages/Help).
  {
    path: routes.HELP_SUPPORT,
    component: Help,
    meta: { public: true, helpPage: true, helpContact: 'support' }
  },
  {
    path: routes.HELP_BUG,
    component: Help,
    meta: { public: true, helpPage: true, helpContact: 'bug' }
  },
  {
    path: routes.HELP_SUGGESTION,
    component: Help,
    meta: { public: true, helpPage: true, helpContact: 'suggestion' }
  },
  {
    path: `${routes.HELP}/:topic?/:question?`,
    component: Help,
    meta: { public: true, helpPage: true }
  },
  {
    path: '/impressum',
    component: Impressum,
    meta: { public: true }
  },
  {
    path: '/sitemap*',
    component: Sitemap,
    meta: { public: true }
  },
  {
    path: routes.COOKIE_POLICY,
    component: CookiePolicy,
    meta: { public: true }
  },
  {
    path: '/unsub/sc/:coverageId/:hash',
    redirect: `${routes.SEARCH_COVERAGE_UNSUBSCRIBE}/:coverageId/:hash`,
    meta: { public: true }
  },
  {
    path: `${routes.SEARCH_COVERAGE_UNSUBSCRIBE}/:coverageId/:hash`,
    component: SearchCoverageUnsubscribe,
    meta: { public: true }
  },
  {
    path: routes.BOOST_SUMMARY,
    component: BoostSummary,
    beforeEnter: boostSummaryGuard,
    meta: { purchase: true }
  },
  {
    path: routes.HOME,
    component: StartPageRouter,
    beforeEnter: ensureGrowthbookReady,
    meta: { public: true }
  },
  {
    path: '/buy-boost',
    component: BuyBoost,
    meta: { purchase: true }
  },
  {
    path: '/buy-super-interest',
    component: BuySuperInterest,
    beforeEnter: buySuperInterestGuard,
    meta: { purchase: true }
  },
  {
    path: '/empty-page',
    component: EmptyPage
  },
  {
    path: routes.PROPOSITION_NOT_FOUND,
    component: PropositionNotFound,
    meta: { public: true }
  },
  {
    path: routes.NOT_FOUND,
    component: PageNotFound,
    meta: { public: true }
  },
  {
    path: '*',
    component: PageNotFound,
    meta: { public: true }
  }
];

export default routeTable;
